import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    collection,
    onSnapshot,
    query,
    where,
    doc,
    getDoc,
    getDocs,
    orderBy,
    documentId
} from 'firebase/firestore';
import {
    BookText,
    Loader2,
    Info,
    User,
    Users,
    ClipboardList,
    TrendingUp,
    Filter,
    Check,
    ChevronDown
} from 'lucide-react';

// --- FONCTIONS UTILITAIRES (Héritées de MyNotes.txt) ---
const getCurrentSchoolYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0 = Jan, 9 = Oct

    if (month >= 8) {
        return `${year}-${year + 1}`;
    }
    return `${year - 1}-${year}`;
};
const CURRENT_SCHOOL_YEAR = getCurrentSchoolYear();
const FIRESTORE_IN_QUERY_LIMIT = 10; // Limite pour les requêtes 'in' de Firestore

// ----------------------------------------------------------------------------------
// --- COMPOSANT PRINCIPAL UNIFIÉ ---
// ----------------------------------------------------------------------------------
const MyNotes = ({ db, setError, setMessage, schoolId, currentUserId, userRole }) => {

    // --- États Partagés ---
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [masterMatieres, setMasterMatieres] = useState([]);
    const [classConfigMap, setClassConfigMap] = useState({}); // Map pour stocker les configs par classe

    // --- États Spécifiques Élève (Si l'utilisateur est l'élève lui-même) ---
    const [eleveIdsToQuery, setEleveIdsToQuery] = useState([]); // IDs utilisés pour la requête eleve/user
    const [studentClasseId, setStudentClasseId] = useState(null);

    // --- États Spécifiques Parent ---
    const [children, setChildren] = useState([]); // Liste des enfants liés
    const [selectedChildId, setSelectedChildId] = useState(''); // ID de l'enfant sélectionné (eleveId)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const isParentRole = userRole === 'parent' || userRole === 'superAdmin';

    // --- Étape 0: Récupérer les matières maîtres et les configurations ---
    useEffect(() => {
        if (!db || !schoolId) return;

        // 1. Charger les matières maîtres
        const masterMatieresRef = collection(db, 'schools', schoolId, 'master_matieres');
        const unsubscribeMaster = onSnapshot(masterMatieresRef, (snapshot) => {
            const matieresData = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                name: doc.data().name 
            }));
            setMasterMatieres(matieresData);
        }, (err) => {
            console.error("Erreur lors du chargement des matières maîtres:", err);
            setError("Erreur lors du chargement des matières.");
        });

        // 2. Charger toutes les configurations de matières par classe
        const configRef = collection(db, 'schools', schoolId, 'class_matiere_config');
        const unsubscribeConfig = onSnapshot(configRef, (snapshot) => {
            const configs = {};
            
            snapshot.docs.forEach(doc => {
                const configData = doc.data();
                const classId = configData.classId;
                
                if (!configs[classId]) {
                    configs[classId] = {};
                }
                
                configs[classId][configData.matiereId] = {
                    coefficient: configData.coefficient,
                    noteSur: configData.noteSur,
                    matiereName: configData.matiereName
                };
            });
            
            setClassConfigMap(configs);
        }, (err) => {
            console.error("Erreur lors du chargement des configurations:", err);
            setError("Erreur lors du chargement des coefficients.");
        });

        return () => {
            unsubscribeMaster();
            unsubscribeConfig();
        };
    }, [db, schoolId, setError]);

    // Fonction pour obtenir les détails d'une matière avec ses coefficients
    const getMatiereDetails = useCallback((matiereId, classeId) => {
        const matiere = masterMatieres.find(m => m.id === matiereId);
        const config = classConfigMap[classeId]?.[matiereId];
        
        return {
            name: matiere?.name || 'Matière inconnue',
            coefficient: config?.coefficient || 1,
            noteSur: config?.noteSur || 20
        };
    }, [masterMatieres, classConfigMap]);

    // ----------------------------------------------------------------------------------
    // --- LOGIQUE PARENT: Récupérer les IDs des enfants liés ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        if (!currentUserId || !schoolId || !db || !isParentRole) {
            setChildren([]);
            return;
        }

        const fetchChildrenData = async () => {
            try {
                // 1. Récupérer les IDs des enfants liés au parent
                const userDocRef = doc(db, 'users', currentUserId);
                const userDocSnap = await getDoc(userDocRef);

                if (!userDocSnap.exists() || !userDocSnap.data().childrenEleveIds || userDocSnap.data().childrenEleveIds.length === 0) {
                    setMessage("Aucun enfant n'est actuellement lié à votre compte.");
                    setChildren([]);
                    return;
                }

                const linkedEleveIds = userDocSnap.data().childrenEleveIds.filter(id => id && typeof id === 'string');

                if (linkedEleveIds.length === 0) {
                    setChildren([]);
                    return;
                }

                const chunks = [];
                for (let i = 0; i < linkedEleveIds.length; i += FIRESTORE_IN_QUERY_LIMIT) {
                    chunks.push(linkedEleveIds.slice(i, i + FIRESTORE_IN_QUERY_LIMIT));
                }

                let eleveDetailsMap = {};
                let classIdsToFetch = new Set();

                // 2. Récupérer les détails des élèves et les inscriptions en parallèle par chunks
                for (const chunk of chunks) {
                    // Requête pour les documents élèves
                    const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
                    const elevesQuery = query(elevesCollectionRef, where(documentId(), 'in', chunk));
                    const elevesSnap = await getDocs(elevesQuery);

                    elevesSnap.docs.forEach(eleveDoc => {
                        const eleveData = eleveDoc.data();
                        const eleveId = eleveDoc.id;
                        eleveDetailsMap[eleveId] = {
                            id: eleveId,
                            name: eleveData.name || `${eleveData.firstName || ''} ${eleveData.lastName || ''}`.trim() || eleveId,
                            className: 'Chargement...'
                        };
                    });

                    // Requête pour les inscriptions des mêmes élèves pour l'année scolaire en cours
                    const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                    const enrollmentQuery = query(
                        enrollmentCollectionRef,
                        where('studentId', 'in', chunk),
                        where('schoolYear', '==', CURRENT_SCHOOL_YEAR)
                    );
                    const enrollmentSnap = await getDocs(enrollmentQuery);

                    enrollmentSnap.docs.forEach(enrollmentDoc => {
                        const enrollmentData = enrollmentDoc.data();
                        if (eleveDetailsMap[enrollmentData.studentId]) {
                            eleveDetailsMap[enrollmentData.studentId].classId = enrollmentData.classId;
                            if (enrollmentData.classId) {
                                classIdsToFetch.add(enrollmentData.classId);
                            }
                        }
                    });
                }

                // 3. Récupérer les noms des classes
                let classNamesMap = {};
                if (classIdsToFetch.size > 0) {
                    const uniqueClassIds = Array.from(classIdsToFetch);
                    const classChunks = [];
                    for (let i = 0; i < uniqueClassIds.length; i += FIRESTORE_IN_QUERY_LIMIT) {
                        classChunks.push(uniqueClassIds.slice(i, i + FIRESTORE_IN_QUERY_LIMIT));
                    }

                    for (const classChunk of classChunks) {
                        const classesCollectionRef = collection(db, 'schools', schoolId, 'classes');
                        const classesQuery = query(classesCollectionRef, where(documentId(), 'in', classChunk));
                        const classesSnap = await getDocs(classesQuery);
                        classesSnap.docs.forEach(classDoc => {
                            classNamesMap[classDoc.id] = classDoc.data().nom || classDoc.data().name || 'Inconnue';
                        });
                    }
                }

                // 4. Combiner toutes les données
                let finalChildren = linkedEleveIds.map(eleveId => {
                    const child = eleveDetailsMap[eleveId] || { id: eleveId, name: eleveId, className: 'Inconnu' };
                    child.className = child.classId ? classNamesMap[child.classId] || 'Inconnue' : 'Inconnue';
                    return child;
                });

                setChildren(finalChildren);
                if (finalChildren.length > 0 && !selectedChildId) {
                    setSelectedChildId(finalChildren[0].id);
                }

            } catch (err) {
                console.error("Erreur lors de la récupération des enfants liés (Parent View):", err);
                setError("Erreur lors du chargement des données de vos enfants.");
                setChildren([]);
            }
        };

        fetchChildrenData();
    }, [db, currentUserId, schoolId, isParentRole, setError, setMessage, selectedChildId]);

    // ----------------------------------------------------------------------------------
    // --- LOGIQUE ÉLÈVE: Récupérer les IDs de l'élève ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        if (isParentRole || !currentUserId || !schoolId || !db) {
            return;
        }

        const fetchUserAndEleveIds = async () => {
            let potentialEleveIds = [currentUserId];
            let classeIdFromEnrollment = null;

            try {
                // 1. Récupérer les données utilisateur
                const userDocRef = doc(db, 'users', currentUserId);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    const legacyId = userData.legacyEleveId;

                    if (legacyId && legacyId !== currentUserId) {
                        potentialEleveIds.push(legacyId);
                    }
                }

                // 2. Tenter de trouver l'ID Eleve réel et le classeId via Enrollment
                const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                const AUTH_UID_FIELD_NAME = 'studentAuthUID';

                const activeEnrollmentQuery = query(
                    enrollmentCollectionRef,
                    where(AUTH_UID_FIELD_NAME, '==', currentUserId),
                    where('schoolYear', '==', CURRENT_SCHOOL_YEAR)
                );
                const enrollmentSnapshot = await getDocs(activeEnrollmentQuery);

                if (!enrollmentSnapshot.empty) {
                    const enrollmentData = enrollmentSnapshot.docs[0].data();
                    const actualEleveIdFound = enrollmentData.studentId;
                    classeIdFromEnrollment = enrollmentData.classId;

                    if (actualEleveIdFound && !potentialEleveIds.includes(actualEleveIdFound)) {
                        potentialEleveIds.push(actualEleveIdFound);
                    }

                    if (classeIdFromEnrollment) {
                        setStudentClasseId(classeIdFromEnrollment);
                    } else {
                        setStudentClasseId(null);
                    }
                } else {
                    setStudentClasseId(userDocSnap.exists() ? userDocSnap.data().classeId : null);
                }

                const finalIds = [...new Set(potentialEleveIds)].filter(id => id && typeof id === 'string');
                setEleveIdsToQuery(finalIds);

            } catch (err) {
                console.error("Erreur lors de la récupération des IDs élève/utilisateur:", err);
                setError("Erreur de connexion aux données utilisateur.");
                setEleveIdsToQuery([]);
            }
        };

        fetchUserAndEleveIds();
    }, [db, currentUserId, schoolId, isParentRole, setError]);

    // ----------------------------------------------------------------------------------
    // --- Étape 3: Écouter les notes (Logique unifiée) ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        let eleveIdsForQuery = [];
        let queryField = 'studentAuthUID';
        let queryValue = currentUserId;

        if (isParentRole) {
            // Vue Parent: on query toutes les notes de l'enfant sélectionné
            if (!selectedChildId) {
                setNotes([]);
                setIsLoading(false);
                return;
            }
            eleveIdsForQuery = [selectedChildId];
            queryField = 'eleveId';
            queryValue = selectedChildId;
        } else {
            // Vue Élève: on query les notes de l'élève lui-même (via AuthUID)
            if (eleveIdsToQuery.length === 0) {
                setNotes([]);
                setIsLoading(false);
                return;
            }
            queryField = 'studentAuthUID';
            queryValue = currentUserId;
        }

        setIsLoading(true);
        setError(null);

        try {
            const notesCollectionRef = collection(db, `schools/${schoolId}/notes`);
            let q;

            q = query(
                notesCollectionRef,
                where(queryField, '==', queryValue),
                orderBy('anneeScolaire', 'desc'),
                orderBy('trimestre', 'desc')
            );

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const fetchedNotes = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setNotes(fetchedNotes);
                setIsLoading(false);
            }, (error) => {
                console.error("Erreur lors de la récupération des notes:", error);
                setError("Erreur lors du chargement de l'historique des notes.");
                setIsLoading(false);
            });

            return () => unsubscribe();
        } catch (e) {
            console.error("Erreur de setup Firestore pour les notes:", e);
            setError("Erreur de configuration de la base de données.");
            setIsLoading(false);
        }
    }, [db, schoolId, isParentRole, selectedChildId, eleveIdsToQuery, currentUserId, setError]);

    // --- Fonction pour obtenir le nom de la matière ---
    const getMatiereName = useCallback((matiereId) => {
        const matiere = masterMatieres.find(m => m.id === matiereId);
        return matiere?.name || matiereId || 'Matière inconnue';
    }, [masterMatieres]);

    // --- Détermination des données et stats affichées ---

    // 1. Déterminer l'entité et les notes à afficher
    const entityToDisplay = useMemo(() => {
        if (isParentRole) {
            return children.find(c => c.id === selectedChildId);
        }
        return {
            id: currentUserId,
            name: `Votre Compte`,
            className: studentClasseId || 'Inconnue',
            classId: studentClasseId // Ajouter classId pour l'élève
        };
    }, [isParentRole, children, selectedChildId, currentUserId, studentClasseId]);

    // Ajouter classId à l'entité pour le parent aussi
    const entityWithClassId = useMemo(() => {
        if (!entityToDisplay) return null;
        
        if (isParentRole) {
            const child = children.find(c => c.id === entityToDisplay.id);
            return {
                ...entityToDisplay,
                classId: child?.classId || null
            };
        }
        return {
            ...entityToDisplay,
            classId: studentClasseId
        };
    }, [entityToDisplay, children, isParentRole, studentClasseId]);

    // 2. Filtrer les notes pour l'entité sélectionnée
    const filteredNotes = useMemo(() => {
        if (!entityToDisplay) return [];

        if (isParentRole) {
            return notes.filter(note => note.eleveId === entityToDisplay.id);
        }
        return notes.filter(note => note.studentAuthUID === entityToDisplay.id);
    }, [notes, isParentRole, entityToDisplay]);

    // 3. Calcul des statistiques de base avec coefficients
    const statsSummary = useMemo(() => {
        if (filteredNotes.length === 0) {
            return { average: 0, count: 0, weightedAverage: 0 };
        }

        let totalCoefficients = 0;
        let totalWeightedPoints = 0;
        let totalPoints20 = 0;
        
        filteredNotes.forEach(note => {
            const val = parseFloat(note.valeurNote) || 0;
            const matiereId = note.matiereId;
            const classeId = note.classeId || entityWithClassId?.classId;
            
            // Obtenir les détails de la matière
            const matiereDetails = getMatiereDetails(matiereId, classeId);
            const coefficient = matiereDetails.coefficient || 1;
            const noteSur = matiereDetails.noteSur || 20;
            
            // Calcul pour moyenne pondérée
            totalCoefficients += coefficient;
            
            // Convertir la note sur 20 pour le calcul
            const noteOn20 = noteSur !== 0 ? (val / noteSur) * 20 : 0;
            totalWeightedPoints += noteOn20 * coefficient;
            
            // Pour la moyenne simple
            totalPoints20 += noteOn20;
        });

        const count = filteredNotes.length;
        const average = count > 0 ? (totalPoints20 / count).toFixed(2) : 0;
        const weightedAverage = totalCoefficients > 0 ? (totalWeightedPoints / totalCoefficients).toFixed(2) : 0;

        return {
            average: parseFloat(average),
            weightedAverage: parseFloat(weightedAverage),
            count: count,
            totalCoefficients: totalCoefficients
        };
    }, [filteredNotes, entityWithClassId, getMatiereDetails]);

    // Fonction pour afficher la note avec son coefficient
    const renderNoteWithCoefficient = (note) => {
        const matiereId = note.matiereId;
        const classeId = note.classeId || entityWithClassId?.classId;
        const matiereDetails = getMatiereDetails(matiereId, classeId);
        
        const noteValue = parseFloat(note.valeurNote) || 0;
        const noteSur = matiereDetails.noteSur || 20;
        const coefficient = matiereDetails.coefficient || 1;
        
        const noteOn20 = noteSur !== 0 ? (noteValue / noteSur) * 20 : 0;
        const isPassing = noteOn20 >= 10;

        return {
            displayValue: noteValue.toFixed(2),
            noteSur,
            coefficient,
            isPassing,
            matiereName: getMatiereName(matiereId)
        };
    };

    // --- Rendu ---

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-48">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mr-3" />
                <p className="text-gray-600">Chargement du relevé de notes...</p>
            </div>
        );
    }

    // --- Cas d'absence d'enfant/d'élève ---
    if (isParentRole && children.length === 0) {
         return (
            <div className="p-10 text-center bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl shadow-lg max-w-4xl mx-auto">
                <Users className="w-8 h-8 mx-auto mb-3" />
                <p className="text-lg font-semibold">Aucun Enfant Lié</p>
                <p>Veuillez contacter l'administration pour lier vos enfants à votre compte afin de consulter leurs notes.</p>
            </div>
        );
    }

    if (!isParentRole && eleveIdsToQuery.length === 0) {
         return (
            <div className="p-10 text-center bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl shadow-lg max-w-4xl mx-auto">
                <User className="w-8 h-8 mx-auto mb-3" />
                <p className="text-lg font-semibold">Information Élève Manquante</p>
                <p>Votre compte n'est pas correctement lié à une fiche élève pour cette année scolaire ou aucune inscription active n'a été trouvée. Contactez l'administration.</p>
            </div>
        );
    }

    const isEntitySelected = isParentRole ? !!selectedChildId : !!entityToDisplay.id;

    return (
        <div className="space-y-8 p-6 bg-white rounded-2xl shadow-2xl max-w-6xl mx-auto">

            {/* TITRE ET SÉLECTION (POUR PARENT) */}
            <div className="border-b pb-4 flex flex-col md:flex-row items-start md:items-center justify-between">
                <h2 className="text-3xl font-extrabold text-gray-900 flex items-center mb-4 md:mb-0">
                    <BookText className="w-8 h-8 mr-3 text-indigo-600" />
                    {isParentRole ? "Relevé de Notes de mes Enfants" : "Mon Relevé de Notes"}
                </h2>

                {/* Sélecteur d'enfant (UNIQUEMENT POUR PARENT) */}
                {isParentRole && children.length > 0 && (
                     <div className="relative w-full md:w-64 z-10">
                        <button
                            onClick={() => children.length > 1 && setIsDropdownOpen(!isDropdownOpen)}
                            className={`w-full text-left bg-indigo-50 border ${children.length > 1 ? 'border-indigo-300 hover:border-indigo-500' : 'border-indigo-200 cursor-default'} rounded-xl p-3 flex items-center justify-between shadow-sm transition duration-150`}
                            title="Sélectionner un enfant"
                        >
                            <div className='flex items-center'>
                                <Filter className="w-5 h-5 mr-2 text-indigo-600" />
                                {entityToDisplay ? (
                                    <span className="font-semibold text-gray-800">
                                        {entityToDisplay.name}
                                    </span>
                                ) : (
                                    <span className="text-gray-500">Choisir un enfant...</span>
                                )}
                            </div>
                            {children.length > 1 && <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />}
                        </button>

                        {isDropdownOpen && children.length > 1 && (
                            <div className="absolute w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-60 overflow-y-auto">
                                {children.map(child => (
                                    <div
                                        key={child.id}
                                        onClick={() => {
                                            setSelectedChildId(child.id);
                                            setIsDropdownOpen(false);
                                        }}
                                        className={`p-3 cursor-pointer hover:bg-indigo-100 flex items-center justify-between transition duration-150 ${selectedChildId === child.id ? 'bg-indigo-200 font-bold' : ''}`}
                                    >
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{child.name}</p>
                                            <p className="text-xs text-gray-500">Classe: {child.className}</p>
                                        </div>
                                        {selectedChildId === child.id && <Check className="w-4 h-4 text-indigo-600" />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {isEntitySelected && (
                <div className="space-y-6">
                    {/* EN-TÊTE ENTITÉ SÉLECTIONNÉE */}
                    <div className="p-4 bg-white border-l-4 border-indigo-500 shadow-lg rounded-r-xl">
                        <p className="text-xl font-bold text-gray-900 flex items-center">
                            <User className="w-6 h-6 mr-2 text-indigo-500" />
                            {entityToDisplay.name}
                        </p>
                        <p className="text-sm text-gray-600">
                            Classe: <span className="font-medium text-indigo-600">{entityToDisplay.className}</span> | 
                            ID Élève: <span className="font-mono text-sm">{entityToDisplay.id}</span>
                        </p>
                    </div>

                    {/* Carte de Statistiques */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* ID Élève (pour l'élève) */}
                         {!isParentRole && (
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl shadow-md flex items-center">
                                <User size={24} className="text-gray-500 mr-3" />
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Mon ID Utilisateur</p>
                                    <span className="text-sm font-semibold text-gray-700 break-all">{currentUserId}</span>
                                </div>
                            </div>
                        )}

                        {/* Moyenne Générale (Simple) */}
                        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl shadow-md flex items-center">
                            <TrendingUp size={24} className="text-indigo-600 mr-3" />
                            <div>
                                <p className="text-sm font-medium text-indigo-700">Moyenne Générale (sur 20)</p>
                                <span className="text-2xl font-bold text-indigo-600">
                                    {statsSummary.average.toLocaleString('fr-FR')} / 20
                                </span>
                            </div>
                        </div>

                        {/* Moyenne Pondérée */}
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl shadow-md flex items-center">
                            <TrendingUp size={24} className="text-purple-600 mr-3" />
                            <div>
                                <p className="text-sm font-medium text-purple-700">Moyenne Pondérée (sur 20)</p>
                                <span className="text-2xl font-bold text-purple-600">
                                    {statsSummary.weightedAverage.toLocaleString('fr-FR')} / 20
                                </span>
                            </div>
                        </div>

                        {/* Nombre de Notes */}
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl shadow-md flex items-center">
                            <ClipboardList size={24} className="text-blue-600 mr-3" />
                            <div>
                                <p className="text-sm font-medium text-blue-700">Total des Notes</p>
                                <span className="text-2xl font-bold text-blue-600">
                                    {statsSummary.count}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tableau des Notes */}
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 mt-6">
                        <div className="p-4 bg-indigo-50 border-b border-indigo-200 text-indigo-800 font-semibold">
                            Détail des Notes de {isParentRole ? entityToDisplay.name : "votre Compte"}
                        </div>
                        {filteredNotes.length === 0 ? (
                            <div className="p-6 text-center text-gray-500 flex items-center justify-center">
                                <Info className="w-5 h-5 mr-2" />
                                Aucune note n'a encore été enregistrée.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matière</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note (/Max)</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coefficient</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trimestre</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année Scolaire</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commentaire</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredNotes.map((note) => {
                                            const noteInfo = renderNoteWithCoefficient(note);
                                            
                                            return (
                                                <tr key={note.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {noteInfo.matiereName}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-lg font-bold"
                                                        style={{ color: noteInfo.isPassing ? '#10B981' : '#EF4444' }}>
                                                        {noteInfo.displayValue} / {noteInfo.noteSur}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                                        <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-bold">
                                                            {noteInfo.coefficient}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {note.typeEvaluation || 'Devoir'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {note.trimestre || 'N/A'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {note.anneeScolaire || 'N/A'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-500 max-w-xs">
                                                        {note.commentaire || 'Aucun'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <p className='text-xs text-gray-400 p-2 border-t mt-4'>
                **Note technique:** Système de notes mis à jour avec le nouveau modèle de matières et coefficients.
                {isParentRole ?
                    ` Consultation des notes de l'enfant sélectionné (${entityToDisplay?.id || 'N/A'}).` :
                    ` Consultation de vos notes personnelles avec coefficients appliqués.`
                }
            </p>
        </div>
    );
};

export default MyNotes;